// Death's Door / Death Blow rules. Pure. No React, no Three.js.
// Owns the predicates for "is this player at Death's Door?",
// "would this damage be a death blow?", and the arithmetic of
// applying life loss with the floor at 0.
//
// ── Codex anchors ──
//
//   Life. "Avatars have life. An avatar's minimum life is zero,
//     and cannot go below that. When an avatar reaches zero life, it
//     is on Death's Door."
//
//   Death's Door. "When your life is reduced to 0, you don't
//     instantly lose the game! Instead, you are at death's door.
//     While at death's door, you can no longer heal or set your life
//     to a higher number. The moment you reach 0 life, you can't die
//     that turn. After that single turn, any damage dealt to your
//     avatar is a death blow."
//
//   Death Blow. "A death blow is any damage dealt to an Avatar who
//     is on death's door, other than on the turn they reached death's
//     door. If you deal a death blow to your opponent's avatar, you
//     win! ... Note that attacking their sites causes your opponent's
//     avatar to lose life, which is different from damaging their
//     avatar. Losing life, including due to site damage, does not
//     cause a death blow."
//
// ── Damage routing per codex ──
//
//   Minion damage   → cardInstance.currentLife (existing combat path).
//   Avatar damage   → controller life loss; if at DD and not immune
//                     this turn → DEATH BLOW (game over).
//   Site damage     → controller life loss (never a death blow).
//   Healing         → blocked when at DD ("can no longer heal").

/** True iff player is at Death's Door (life clamped at 0). */
export function isAtDeathsDoor(playerLife) {
  return (playerLife || 0) <= 0;
}

/**
 * Pure life-loss arithmetic. Caller is responsible for the immunity
 * check (immunity prevents Avatar damage from becoming a death blow,
 * but doesn't change life-loss arithmetic for site damage etc.).
 *
 * @returns { nextLife, enteredDeathsDoor }
 *   enteredDeathsDoor: true iff this damage took the player from
 *   above zero down to zero (the trigger moment for immunity).
 */
export function applyLifeLoss(currentLife, amount) {
  const damage = Math.max(0, amount || 0);
  const next = Math.max(0, (currentLife || 0) - damage);
  return {
    nextLife: next,
    enteredDeathsDoor: (currentLife || 0) > 0 && next === 0,
  };
}

/**
 * True iff direct Avatar damage right now would be a death blow.
 * Requires: player is at DD AND they are NOT immune this turn.
 * Site damage NEVER death-blows; callers should not pass site damage
 * through this predicate.
 */
export function wouldBeDeathBlow(playerLife, immuneThisTurn) {
  if (!isAtDeathsDoor(playerLife)) return false;
  if (immuneThisTurn) return false;
  return true;
}

/**
 * True iff the player can be healed right now. Healing is blocked
 * once at DD ("you can no longer heal or set your life to a higher
 * number"). Use as a guard around any +life effect.
 */
export function canHeal(playerLife) {
  return !isAtDeathsDoor(playerLife);
}

/**
 * Pure healing arithmetic with the Sorcery max-life cap (default 20).
 * Returns the new life value. If the player is at DD, healing is
 * blocked → returns currentLife unchanged.
 */
export function applyHealing(currentLife, amount, maxLife = 20) {
  if (isAtDeathsDoor(currentLife)) return currentLife || 0;
  const healed = Math.max(0, amount || 0);
  const next = Math.min(maxLife, (currentLife || 0) + healed);
  return next;
}
